if (!customElements.get('bundle-builder-reels')) {
  customElements.define('bundle-builder-reels', class extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.videos = [...this.querySelectorAll('video')];
      const connection = navigator.connection;
      const limitPreload = connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const autoplayVideo = this.querySelector('video[data-autoplay]');
      const players = new Map();
      let autoplayAttempted = false;
      let userInteracted = false;
      const hydrate = (video, preload) => {
        video.preload = preload;
        if (!video.dataset.src) return;
        video.src = video.dataset.src;
        delete video.dataset.src;
        video.load();
      };
      this.videos.forEach((video) => {
        const card = video.closest('.bundle-reels__card');
        const button = card.querySelector('.bundle-reels__toggle');
        const soundButton = card.querySelector('.bundle-reels__sound');
        const updateSound = () => {
          const soundOn = !video.muted;
          soundButton.setAttribute('aria-pressed', String(soundOn));
          soundButton.setAttribute('aria-label', `${soundButton.dataset.label}: Ton ${soundOn ? 'ausschalten' : 'einschalten'}`);
        };
        video.muted = true;
        updateSound();
        video.addEventListener('volumechange', updateSound, { signal });
        const update = () => {
          const playing = !video.paused;
          card.classList.toggle('is-playing', playing);
          button.setAttribute('aria-pressed', String(playing));
          button.setAttribute('aria-label', `${button.dataset.label} ${playing ? 'pausieren' : 'abspielen'}`);
        };
        const setLoading = (loading) => {
          card.classList.toggle('is-loading', loading);
          button.setAttribute('aria-busy', String(loading));
        };
        video.addEventListener('waiting', () => { if (!video.paused) setLoading(true); }, { signal });
        video.addEventListener('playing', () => {
          card.classList.add('has-played');
          setLoading(false);
        }, { signal });
        video.addEventListener('play', update, { signal });
        video.addEventListener('pause', () => { setLoading(false); update(); }, { signal });
        video.addEventListener('error', () => {
          video.pause();
          setLoading(false);
          card.classList.remove('has-played');
          update();
        }, { signal });
        const play = async () => {
          this.videos.forEach((other) => { if (other !== video) other.pause(); });
          hydrate(video, 'auto');
          setLoading(true);
          try { await video.play(); } catch { setLoading(false); update(); }
        };
        players.set(video, play);
        button.addEventListener('click', () => {
          userInteracted = true;
          if (!video.paused) video.pause();
          else play();
        }, { signal });
        soundButton.addEventListener('click', () => {
          userInteracted = true;
          video.muted = !video.muted;
          updateSound();
          if (!video.muted && video.paused) play();
        }, { signal });
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !document.hidden) {
              hydrate(entry.target, limitPreload ? 'metadata' : 'auto');
              if (entry.target === autoplayVideo && entry.intersectionRatio >= 0.5 &&
                  !autoplayAttempted && !userInteracted && !limitPreload && !reduceMotion.matches) {
                autoplayAttempted = true;
                players.get(entry.target)();
              }
            } else {
              entry.target.pause();
              // Avoid buffering hidden reels, including after switching to desktop.
              entry.target.preload = 'none';
            }
          });
        }, { threshold: [0, 0.5] });
        this.videos.forEach((video) => this.observer.observe(video));
      }
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.videos.forEach((video) => video.pause());
      }, { signal });
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.videos?.forEach((video) => video.pause());
    }
  });
}
